import React, { useEffect, useState } from 'react';
import { History, Loader2, CheckCircle2, AlertTriangle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { recurringPaymentsApi } from '../../../../services/recurringPaymentsApi';
import { BillingAttempt } from '../../../../types/payments';

interface BillingHistoryProps {
    /** When provided, skip initial fetch and use these attempts. */
    initialAttempts?: BillingAttempt[];
}

export function BillingHistory({ initialAttempts }: BillingHistoryProps = {}) {
    const hasInitial = initialAttempts !== undefined;
    const [history, setHistory] = useState<BillingAttempt[]>(hasInitial ? initialAttempts : []);
    const [loading, setLoading] = useState(!hasInitial);
    const [error, setError] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (hasInitial) {
            setHistory(initialAttempts ?? []);
            setLoading(false);
            return;
        }
        const loadHistory = async () => {
            try {
                const data = await recurringPaymentsApi.getBillingHistory(15);
                setHistory(data.attempts);
            } catch (err) {
                console.error(err);
                setError("Error cargando el historial.");
            } finally {
                setLoading(false);
            }
        };
        loadHistory();
    }, [hasInitial, initialAttempts]);

    if (loading) {
        return (
            <div className="flex justify-center p-8 bg-white border border-slate-200 rounded-2xl shadow-sm">
                <Loader2 className="w-8 h-8 animate-spin text-[#1CB3B3]" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 p-4 rounded-xl text-red-600 text-sm border border-red-200">
                <AlertCircle className="w-5 h-5 inline-block mr-2" />
                {error}
            </div>
        );
    }

    if (history.length === 0) {
        return (
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm text-center">
                <History className="w-10 h-10 text-slate-400 mx-auto mb-3" />

                <h4 className="text-slate-900 font-bold mb-1">
                    Sin historial de pagos recurrentes
                </h4>

                <p className="text-sm text-slate-500">
                    Tus cobros automáticos aparecerán aquí.
                </p>
            </div>
        );
    }

    const statusMap = {
        success: {
            icon: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
            text: 'Aprobado',
            color: 'text-emerald-600'
        },
        failed: {
            icon: <AlertTriangle className="w-4 h-4 text-amber-500" />,
            text: 'Rechazado',
            color: 'text-amber-600'
        },
        error: {
            icon: <AlertCircle className="w-4 h-4 text-red-500" />,
            text: 'Error técnico',
            color: 'text-red-600'
        },
    };

    const formatCOP = (val: number) =>
        val.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

    return (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden transition-all duration-300">

            {/* Cabecera (Clickable) */}
            <div
                className="p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="flex items-center gap-2">
                    <History className="w-5 h-5 text-[#1CB3B3]" />

                    <h3 className="text-xl font-bold text-slate-900">
                        Historial de Cobros
                    </h3>
                </div>

                <div className="text-slate-500 bg-slate-50 p-1.5 rounded-lg border border-slate-200">
                    {isOpen
                        ? <ChevronUp className="w-5 h-5" />
                        : <ChevronDown className="w-5 h-5" />
                    }
                </div>
            </div>

            {/* Contenido (Acordeón) */}
            {isOpen && (
                <div className="divide-y divide-slate-200 border-t border-slate-200 bg-white animate-in slide-in-from-top-2 duration-200">

                    {history.map((attempt) => {
                        const s = statusMap[attempt.status] || statusMap.error;
                        const date = new Date(attempt.created_at);

                        return (
                            <div
                                key={attempt.id}
                                className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                            >
                                <div className="flex items-center gap-4">

                                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-50 border border-slate-200">
                                        {s.icon}
                                    </div>

                                    <div>
                                        <p className="text-slate-900 font-medium">
                                            {formatCOP(attempt.amount)}
                                        </p>

                                        <p className="text-xs text-slate-500 flex items-center gap-1">
                                            {date.toLocaleDateString('es-CO', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                            })}

                                            {' '}a las{' '}

                                            {date.toLocaleTimeString('es-CO', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                </div>

                                <div className="text-right">
                                    <p className={`text-sm font-bold ${s.color}`}>
                                        {s.text}
                                    </p>

                                    {attempt.rejection_type && (
                                        <p className="text-[10px] text-slate-500 uppercase mt-0.5">
                                            Tipo: {attempt.rejection_type}
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                </div>
            )}
        </div>
    );
}

